/** Datasets de referência carregados no boot. */
export type DatasetId = 'waypoints' | 'airspaces' | 'navaids' | 'notams'

export interface DatasetPolicy {
  /**
   * Versão do formato gravado. Muda quando o shape do payload persistido muda — a entrada antiga
   * simplesmente não é encontrada (a versão faz parte da chave), então não existe migração.
   */
  schemaVersion: number
  /**
   * Dentro deste prazo o boot usa o cache sem tocar a rede. Além dele, vai à rede e só cai no cache
   * (sem avisar nada ao usuário) se a rede falhar. É o prazo que define quanto tempo uma mudança
   * publicada na origem leva, no pior caso, para chegar a quem abre o app.
   */
  softTtlMs: number
  /**
   * Além deste prazo o dado só é usado se a rede falhar, e com aviso visível.
   *
   * Dado aeronáutico velho apresentado como atual é risco de segurança de voo. Por outro lado,
   * esconder a camada cria falso negativo ("não há NOTAM" quando o correto é "não sabemos"), então a
   * política é sempre mostrar com a idade explícita — nunca esconder, nunca apagar.
   */
  hardTtlMs: number
  /**
   * Piso de sanidade: uma entrada que produza menos itens que isto é tratada como corrompida
   * (escrita truncada por quota, por exemplo) e descartada. Espelha a guarda que
   * `JetStreamAirspacesRepository` já fazia ("keep previous data if zero features").
   */
  minExpectedItems: number
}

const MINUTE = 60 * 1000
const HOUR = 60 * MINUTE

export const DATASET_POLICIES: Record<DatasetId, DatasetPolicy> = {
  /** Ciclo AIRAC/ROTAER é de 28 dias, e o ADHP aberto é sempre revalidado por ICAO. */
  waypoints: { schemaVersion: 1, softTtlMs: 6 * HOUR, hardTtlMs: 7 * 24 * HOUR, minExpectedItems: 15_000 },
  /**
   * Muda por NOTAM/portaria, e remoção que demora a sumir do mapa é queixa operacional. O `max-age`
   * do CDN (1 h) continua somando por cima do soft TTL.
   */
  airspaces: { schemaVersion: 1, softTtlMs: 15 * MINUTE, hardTtlMs: 24 * HOUR, minExpectedItems: 500 },
  navaids: { schemaVersion: 1, softTtlMs: HOUR, hardTtlMs: 7 * 24 * HOUR, minExpectedItems: 100 },
  /** O mais perecível. O auto-update de 10 min do NotamRepository continua valendo por cima disto. */
  notams: { schemaVersion: 1, softTtlMs: 15 * MINUTE, hardTtlMs: 3 * HOUR, minExpectedItems: 1 }
}
